import clsx from 'clsx'

// ── Button ──────────────────────────────────────────────────────────────────
export function Button({ children, variant = 'primary', size = 'md', className = '', ...props }) {
  const base = 'inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
  const variants = {
    primary:  'btn-specze text-white',
    outline:  'btn-outline',
    ghost:    'btn-ghost',
    danger:   'bg-red-500/15 text-red-400 border border-red-500/20 hover:bg-red-500/25',
  }
  const sizes = {
    sm: 'px-3.5 py-1.5 text-xs',
    md: 'px-5 py-2.5 text-sm',
    lg: 'px-7 py-3.5 text-sm',
  }
  return (
    <button className={clsx(base, variants[variant], sizes[size], className)} {...props}>
      {children}
    </button>
  )
}

// ── Input ───────────────────────────────────────────────────────────────────
export function Input({ label, error, hint, className = '', ...props }) {
  return (
    <div>
      {label && <label className="label-specze">{label}</label>}
      <input className={clsx('input-specze', error && 'input-error', className)} {...props} />
      {hint  && <p className="text-[11px] text-white/35 mt-1.5">{hint}</p>}
      {error && <p className="text-[11px] text-red-400 mt-1.5">{error}</p>}
    </div>
  )
}

// ── Select ──────────────────────────────────────────────────────────────────
export function Select({ label, error, options = [], className = '', ...props }) {
  return (
    <div>
      {label && <label className="label-specze">{label}</label>}
      <select className={clsx('input-specze cursor-pointer', error && 'input-error', className)} {...props}>
        {options.map(o => (
          <option key={o.value} value={o.value} className="bg-cosmic-900">{o.label}</option>
        ))}
      </select>
      {error && <p className="text-[11px] text-red-400 mt-1.5">{error}</p>}
    </div>
  )
}

// ── Textarea ────────────────────────────────────────────────────────────────
export function Textarea({ label, error, rows = 4, className = '', ...props }) {
  return (
    <div>
      {label && <label className="label-specze">{label}</label>}
      <textarea rows={rows} className={clsx('input-specze resize-none', error && 'input-error', className)} {...props} />
      {error && <p className="text-[11px] text-red-400 mt-1.5">{error}</p>}
    </div>
  )
}

// ── Card ────────────────────────────────────────────────────────────────────
export function Card({ children, className = '', hover = false }) {
  return (
    <div className={clsx(hover ? 'glass-card-hover' : 'glass-card', className)}>
      {children}
    </div>
  )
}

// ── Badge ───────────────────────────────────────────────────────────────────
export function StatusBadge({ status }) {
  const map = {
    pending:    'badge-pending',
    generating: 'badge-generating',
    complete:   'badge-complete',
    failed:     'badge-failed',
  }
  return (
    <span className={map[status] ?? 'badge-pending'}>
      {status === 'generating' && <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />}
      {status === 'complete'   && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
      {status === 'failed'     && <span className="w-1.5 h-1.5 rounded-full bg-red-400" />}
      {status}
    </span>
  )
}

// ── Spinner ─────────────────────────────────────────────────────────────────
export function Spinner({ size = 'md', className = '' }) {
  const sizes = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-10 h-10' }
  return (
    <svg className={clsx(sizes[size], 'animate-spin text-purple-400', className)} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"/>
      <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
    </svg>
  )
}

// ── Page header ─────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex items-start justify-between px-8 pt-8 pb-5">
      <div>
        <h1 className="text-xl font-display font-bold text-white tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-white/40 mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-3">{children}</div>}
    </div>
  )
}

// ── Empty state ──────────────────────────────────────────────────────────────
export function EmptyState({ icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center px-6">
      <div className="w-16 h-16 rounded-2xl glass-card flex items-center justify-center mb-5 text-2xl">
        {icon}
      </div>
      <p className="font-semibold text-white mb-1.5">{title}</p>
      <p className="text-sm text-white/40 max-w-xs mb-5">{description}</p>
      {action}
    </div>
  )
}

// ── Alert ────────────────────────────────────────────────────────────────────
export function Alert({ type = 'error', children }) {
  const styles = {
    error:   'bg-red-500/10 border-red-500/20 text-red-400',
    success: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    warning: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
    info:    'bg-blue-500/10 border-blue-500/20 text-blue-400',
  }
  return (
    <div className={clsx('flex items-start gap-3 rounded-xl border px-4 py-3 text-sm', styles[type])}>
      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
      </svg>
      <span>{children}</span>
    </div>
  )
}

// ── Stat card ────────────────────────────────────────────────────────────────
export function StatCard({ label, value, icon, gradient }) {
  return (
    <div className="glass-card p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-xl ${gradient}`}>
        {icon}
      </div>
      <div>
        <p className="text-2xl font-bold text-white tabular-nums">{value}</p>
        <p className="text-xs text-white/40 mt-0.5">{label}</p>
      </div>
    </div>
  )
}
